import React, { useState, useCallback, useEffect, useMemo } from "react";
import type { SortButtonChoice } from "@shopify/polaris";
import { Select, IndexFilters, IndexFiltersMode, ChoiceList } from "@shopify/polaris";
import { getCurrencySymbol, isEmpty, softAssertNumber } from "../../../../helper_functions/utils";
import {
  useUser,
  useHorseVariantVendorsOptions,
  allSuppliers,
  allTypes,
  allVendors,
  allLocations,
  useSuppliersSearchOptions,
  useHorseVariantsProductTypeOptions,
  useShopifyChannelAvailabilityOptions,
  useHorseLocationsOptions,
  getNumberParamValue,
  getParamValue,
  getArrayParamValue,
  useCollections,
} from "../../../../api_utils/requests";
import TagCheckList from "../../../common/Filters/TagCheckList";
import MoreLessFilter from "../../../common/Filters/MoreLessFilter";
import type {
  DecimalRangeFilter,
  HorseVariantsQueryParams,
  NumberRangeFilter,
  Option,
} from "../../../../api_utils/types";
import { HORSE_VARIANTS_COLLECTIONS } from "../../../../api_utils/types";
import type { AppliedFilterInterface } from "../../../../api_utils/types";
import { moreThanLessThanLabel } from "../../../common/Filters/label_tools";
import { CollectionSelect } from "../../../common/Filters/CollectionSelect";

const sortOptions = [
  { label: "Title", directionLabel: "A-Z", value: "title asc" },
  { label: "Title", directionLabel: "Z-A", value: "title desc" },
  { label: "Variant title", directionLabel: "A-Z", value: "variant_title asc" },
  { label: "Variant title", directionLabel: "Z-A", value: "variant_title desc" },
  { label: "SKU", directionLabel: "A-Z", value: "sku asc" },
  { label: "SKU", directionLabel: "Z-A", value: "sku desc" },
  { label: "Vendor", directionLabel: "A-Z", value: "vendor asc" },
  { label: "Vendor", directionLabel: "Z-A", value: "vendor desc" },
  { label: "Type", directionLabel: "A-Z", value: "product_type asc" },
  { label: "Type", directionLabel: "Z-A", value: "product_type desc" },
  { label: "Available", directionLabel: "Low/High", value: "currently_available_quantity asc" },
  { label: "Available", directionLabel: "High/Low", value: "currently_available_quantity desc" },
  { label: "Weight", directionLabel: "Low/High", value: "weight asc" },
  { label: "Weight", directionLabel: "High/Low", value: "weight desc" },
  { label: "Cost", directionLabel: "Low/High", value: "cost asc" },
  { label: "Cost", directionLabel: "High/Low", value: "cost desc" },
  { label: "Price", directionLabel: "Low/High", value: "price asc" },
  { label: "Price", directionLabel: "High/Low", value: "price desc" },
  { label: "Value", directionLabel: "Low/High", value: "value asc" },
  { label: "Value", directionLabel: "High/Low", value: "value desc" },
] as SortButtonChoice[];

const tabs = [
  {
    // sends collection=undiscarded param to backend
    id: "horse-variants-unarchived-tab",
    content: "Unarchived",
    panelID: "horse-variants-unarchived-tab-content",
    accessibilityLabel: "Unarchived variants",
  },
  {
    // sends collection=all param to backend
    id: "horse-variants-all-tab",
    content: "All",
    panelID: "horse-variants-all-tab-content",
    accessibilityLabel: "All variants (includes unarchived and archived variants)",
  },
];

type DisambiguateLabelArgs =
  | {
      key: "horseLocations";
      value: number;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "moreThanLessThan";
      value: DecimalRangeFilter | NumberRangeFilter;
      label: string;
      prefix?: string;
      suffix?: string;
    }
  | {
      key: "productAvailability";
      value: string;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "productTags";
      value: string[];
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "productType";
      value: string;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "supplierId";
      value: number;
      label?: never;
      prefix?: never;
      suffix?: never;
    }
  | {
      key: "vendor";
      value: string;
      label?: never;
      prefix?: never;
      suffix?: never;
    };

const defaultSearchParams = new URLSearchParams();

function HorseVariantSearchFieldAndMoreFiltersComp({
  setFilters,
  selected,
  onSelect,
  initialQuantity,
  initialVendor,
  initialSupplierId,
  initialHorseLocationId,
  parentSetHorseLocationId,
  disableTabs = false,
  searchParams = defaultSearchParams,
  setSearchParams,
}: {
  readonly setFilters: (filters: HorseVariantsQueryParams) => void;
  readonly selected?: number;
  readonly onSelect?: (selected: number) => void;
  readonly initialQuantity?: DecimalRangeFilter;
  readonly initialVendor?: string;
  readonly initialSupplierId?: number;
  readonly initialHorseLocationId?: number;
  // Used to sync the HorseLocation to the parent component, like for a HorseLocationSelect
  readonly parentSetHorseLocationId?: (id: number) => void;
  readonly disableTabs?: boolean;
  readonly searchParams?: URLSearchParams;
  readonly setSearchParams?: (searchParams: URLSearchParams) => void;
}): React.JSX.Element {
  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();

  const initSortedCol = getParamValue(searchParams.get("by_sort[column]"), "title");
  const initSortedColDir = getParamValue(searchParams.get("by_sort[direction]"), "asc");
  const [sort, setSort] = useState([`${initSortedCol} ${initSortedColDir}`]);
  const [collectionId, setCollectionId] = useState<string>(getParamValue(searchParams.get("collection_id")));

  // TODO: This is now just a single pub / unpub filter, not an array of pub and unpub shopify channels
  // Reconstruct productAvailability from URL params
  const publishedIds = getParamValue(searchParams.get("published_shopify_channel_ids"), "");
  const unpublishedIds = getParamValue(searchParams.get("unpublished_shopify_channel_ids"), "");
  const publishedIdsArray = publishedIds ? publishedIds.split(",") : [];
  const unpublishedIdsArray = unpublishedIds ? unpublishedIds.split(",") : [];
  const reconstructedProductAvailability = publishedIds
    ? `${publishedIdsArray.join("|")}:true`
    : unpublishedIds
      ? `${unpublishedIdsArray.join("|")}:false`
      : "";

  const [cost, setCost] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_cost[more_than]")),
    less_than: getParamValue(searchParams.get("by_cost[less_than]")),
  });
  const [price, setPrice] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_price[more_than]")),
    less_than: getParamValue(searchParams.get("by_price[less_than]")),
  });
  const [productAvailability, setProductAvailability] = useState(reconstructedProductAvailability || "");
  const [productTags, setProductTags] = useState(
    getArrayParamValue<string>(searchParams.getAll("product_tags[]"), "string", []),
  );
  const [productType, setProductType] = useState(getParamValue(searchParams.get("product_type"), ""));
  const [productValue, setProductValue] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_value[more_than]")),
    less_than: getParamValue(searchParams.get("by_value[less_than]")),
  });
  const [quantity, setQuantity] = useState<DecimalRangeFilter>(
    initialQuantity || {
      more_than: getParamValue(searchParams.get("by_quantity[more_than]")),
      less_than: getParamValue(searchParams.get("by_quantity[less_than]")),
    },
  );
  const [search, setSearch] = useState(getParamValue(searchParams.get("search"), ""));
  const [supplierId, setSupplierId] = useState<number | undefined>(
    initialSupplierId || getNumberParamValue(searchParams.get("supplier_id")),
  );
  // We have our own `setHorseLocationId` because the `parentSetHorseLocationId` is optional
  const [horseLocationId, setHorseLocationId] = useState<number | undefined>(
    initialHorseLocationId || getNumberParamValue(searchParams.get("horse_location_id")),
  );
  const [totalVolume, setTotalVolume] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_total_volume[more_than]")),
    less_than: getParamValue(searchParams.get("by_total_volume[less_than]")),
  });
  const [vendor, setVendor] = useState<string>(initialVendor || getParamValue(searchParams.get("vendor")));
  const [weight, setWeight] = useState<DecimalRangeFilter>({
    more_than: getParamValue(searchParams.get("by_weight[more_than]")),
    less_than: getParamValue(searchParams.get("by_weight[less_than]")),
  });
  const [mode, setMode] = useState<IndexFiltersMode>(IndexFiltersMode.Default);

  const includeDiscarded = HORSE_VARIANTS_COLLECTIONS[selected];
  const { data: vendors } = useHorseVariantVendorsOptions({ collection: includeDiscarded });
  const { data: productTypes } = useHorseVariantsProductTypeOptions({ collection: includeDiscarded });
  const { data: suppliers } = useSuppliersSearchOptions();
  const { data: shopifyChannels, isLoading: shopifyChannelsLoading } = useShopifyChannelAvailabilityOptions();
  const { data: horseLocations } = useHorseLocationsOptions();
  const { data: collections } = useCollections();

  useEffect(() => {
    const [column, direction] = sort[0].split(" ");
    const shopifyChannelOption = shopifyChannels?.find((channel) => channel.value === productAvailability);
    const published_shopify_channel_ids: number[] = [];
    const unpublished_shopify_channel_ids: number[] = [];

    if (shopifyChannelOption) {
      const [shopifyChannelIds, isPublished] = shopifyChannelOption.value.split(":");
      if (isPublished === "true") {
        published_shopify_channel_ids.push(...shopifyChannelIds.split("|").map(softAssertNumber));
      } else if (isPublished === "false") {
        unpublished_shopify_channel_ids.push(...shopifyChannelIds.split("|").map(softAssertNumber));
      }
    }

    const queryParams: HorseVariantsQueryParams = {
      by_cost: cost,
      horse_location_id: horseLocationId,
      by_price: price,
      published_shopify_channel_ids: published_shopify_channel_ids,
      unpublished_shopify_channel_ids: unpublished_shopify_channel_ids,
      product_tags: productTags,
      product_type: productType,
      by_value: productValue,
      by_quantity: quantity,
      search,
      supplier_id: supplierId,
      by_total_volume: totalVolume,
      vendor: vendor,
      by_weight: weight,
      by_sort: { column, direction },
      collection_id: collectionId, // Add collection_id to query params
    };

    const filteredSearchParams = new URLSearchParams();

    Object.entries({
      "by_cost[more_than]": cost.more_than,
      "by_cost[less_than]": cost.less_than,
      horse_location_id: horseLocationId,
      "by_price[more_than]": price.more_than,
      "by_price[less_than]": price.less_than,
      published_shopify_channel_ids: published_shopify_channel_ids.length
        ? published_shopify_channel_ids.join(",")
        : undefined,
      unpublished_shopify_channel_ids: unpublished_shopify_channel_ids.length
        ? unpublished_shopify_channel_ids.join(",")
        : undefined,
      product_tags: productTags.length ? productTags.join(",") : undefined,
      product_type: productType,
      "by_value[more_than]": productValue.more_than,
      "by_value[less_than]": productValue.less_than,
      "by_quantity[more_than]": quantity.more_than,
      "by_quantity[less_than]": quantity.less_than,
      search,
      supplier_id: supplierId,
      "by_total_volume[more_than]": totalVolume.more_than,
      "by_total_volume[less_than]": totalVolume.less_than,
      vendor: vendor,
      "by_weight[more_than]": weight.more_than,
      "by_weight[less_than]": weight.less_than,
      // Only include if not default values
      ...(column !== "title" ? { "by_sort[column]": column } : {}),
      ...(direction !== "asc" ? { "by_sort[direction]": direction } : {}),
      collection_id: collectionId,
    }).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // For arrays, only set if they have at least one element.
          if (value.length > 0) {
            filteredSearchParams.set(key, value.join(","));
          }
        } else if (typeof value === "string") {
          // For strings, only set if not empty.
          if (value !== "") {
            filteredSearchParams.set(key, value);
          }
        } else {
          // For numbers and other types (e.g. dates), just convert to string.
          filteredSearchParams.set(key, String(value));
        }
      }
    });

    setSearchParams?.(filteredSearchParams);
    setFilters(queryParams);
  }, [
    setFilters,
    setSearchParams,
    collectionId,
    cost,
    horseLocationId,
    price,
    productAvailability,
    productTags,
    productType,
    productValue,
    quantity,
    search,
    shopifyChannels,
    sort,
    supplierId,
    totalVolume,
    vendor,
    weight,
  ]);

  const handleVendorChange = useCallback((value: string) => {
    setVendor(value === allVendors ? undefined : value);
  }, []);
  const handleSupplierChange = useCallback((value: string) => {
    setSupplierId(value === allSuppliers ? undefined : softAssertNumber(value));
  }, []);
  const handleProductTypeChange = useCallback((value: string) => {
    setProductType(value === allTypes ? undefined : value);
  }, []);
  const handleHorseLocationIdChange = useCallback(
    (newHorseLocationId: string) => {
      setHorseLocationId(newHorseLocationId === allLocations ? undefined : softAssertNumber(newHorseLocationId));
      parentSetHorseLocationId?.(
        newHorseLocationId === allLocations ? undefined : softAssertNumber(newHorseLocationId),
      );
    },
    [setHorseLocationId, parentSetHorseLocationId],
  );
  const handleNewTagAddition = useCallback(
    (newTag) => {
      const payload = [...productTags, newTag];
      setProductTags(payload);
    },
    [productTags],
  );

  const handleCostRemove = useCallback(() => {
    setCost({ more_than: undefined, less_than: undefined });
  }, []);
  const handleHorseLocationRemove = useCallback(() => {
    setHorseLocationId(undefined);
    parentSetHorseLocationId?.(undefined);
  }, [setHorseLocationId, parentSetHorseLocationId]);
  const handlePriceRemove = useCallback(() => {
    setPrice({ more_than: undefined, less_than: undefined });
  }, []);
  const handleProductAvailabilityRemove = useCallback(() => {
    setProductAvailability("");
  }, []);
  const handleProductTagsRemove = useCallback(() => {
    setProductTags([]);
  }, []);
  const handleProductTypeRemove = useCallback(() => {
    setProductType(undefined);
  }, []);
  const handleProductValueRemove = useCallback(() => {
    setProductValue({ more_than: undefined, less_than: undefined });
  }, []);
  const handleQuantityRemove = useCallback(() => {
    setQuantity({ more_than: undefined, less_than: undefined });
  }, []);
  const handleSearchRemove = useCallback(() => {
    setSearch("");
  }, []);
  const handleSupplierIdRemove = useCallback(() => {
    setSupplierId(undefined);
  }, []);
  const handleTotalVolumeRemove = useCallback(() => {
    setTotalVolume({ more_than: undefined, less_than: undefined });
  }, []);
  const handleVendorRemove = useCallback(() => {
    setVendor(undefined);
  }, []);
  const handleWeightRemove = useCallback(() => {
    setWeight({ more_than: undefined, less_than: undefined });
  }, []);
  const handleCollectionRemove = useCallback(() => {
    setCollectionId(undefined);
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    handleCostRemove();
    handleHorseLocationRemove();
    handlePriceRemove();
    handleProductAvailabilityRemove();
    handleProductTagsRemove();
    handleProductTypeRemove();
    handleProductValueRemove();
    handleQuantityRemove();
    handleSearchRemove();
    handleSupplierIdRemove();
    handleTotalVolumeRemove();
    handleVendorRemove();
    handleWeightRemove();
    handleCollectionRemove();
  }, [
    handleCostRemove,
    handleHorseLocationRemove,
    handlePriceRemove,
    handleProductAvailabilityRemove,
    handleProductTagsRemove,
    handleProductTypeRemove,
    handleProductValueRemove,
    handleQuantityRemove,
    handleSearchRemove,
    handleSupplierIdRemove,
    handleTotalVolumeRemove,
    handleVendorRemove,
    handleWeightRemove,
    handleCollectionRemove,
  ]);

  const disambiguateLabel = useCallback(
    ({ key, value, label = "", prefix = "", suffix = "" }: DisambiguateLabelArgs): string => {
      switch (key) {
        case "vendor":
          return `Vendor is ${value}`;
        case "supplierId":
          return `Supplier is ${suppliers.find((sup) => sup.value === value.toString())?.label || value}`;
        case "productType":
          return `Type: ${value}`;
        case "productTags":
          return `Tags: ${value.join(", ")}`;
        case "productAvailability":
          return value;
        case "horseLocations":
          return `Location is ${horseLocations.find((hlo) => hlo.value === value.toString())?.label || value}`;
        case "moreThanLessThan":
          return moreThanLessThanLabel(value, label, prefix, suffix);
        default:
          throw new Error(`Unknown key: ${key as any}`);
      }
    },
    [suppliers, horseLocations],
  );

  // TODO: totalVolume
  const filters = [
    {
      key: "collection",
      label: "Collection",
      filter: <CollectionSelect collectionId={collectionId} setCollectionId={setCollectionId} />,
    },
    {
      key: "vendor",
      label: "Vendor",
      filter: (
        <Select
          label=""
          labelHidden
          onChange={handleVendorChange}
          options={vendors satisfies Option[]}
          value={vendor || allVendors}
        />
      ),
    },
    {
      key: "supplierId",
      label: "Supplier",
      filter: (
        <Select
          label=""
          labelHidden
          onChange={handleSupplierChange}
          options={suppliers satisfies Option[]}
          value={supplierId?.toString() || allSuppliers}
        />
      ),
    },
    {
      key: "productType",
      label: "Type",
      filter: (
        <Select
          label=""
          labelHidden
          onChange={handleProductTypeChange}
          options={productTypes satisfies Option[]}
          value={productType || allTypes}
        />
      ),
    },
    {
      key: "cost",
      label: "Cost",
      filter: (
        <MoreLessFilter
          align="right"
          less_than={cost.less_than}
          more_than={cost.more_than}
          prefix={getCurrencySymbol(userCurrency)}
          setStateCallback={setCost}
          targetState={cost}
        />
      ),
    },
    {
      key: "price",
      label: "Price",
      filter: (
        <MoreLessFilter
          align="right"
          less_than={price.less_than}
          more_than={price.more_than}
          prefix={getCurrencySymbol(userCurrency)}
          setStateCallback={setPrice}
          targetState={price}
        />
      ),
    },
    {
      key: "productValue",
      label: "Inventory value",
      filter: (
        <MoreLessFilter
          align="right"
          less_than={productValue.less_than}
          more_than={productValue.more_than}
          prefix={getCurrencySymbol(userCurrency)}
          setStateCallback={setProductValue}
          targetState={productValue}
        />
      ),
    },
    {
      key: "weight",
      label: "Weight",
      filter: (
        <MoreLessFilter
          less_than={weight.less_than}
          more_than={weight.more_than}
          setStateCallback={setWeight}
          suffix="g"
          targetState={weight}
        />
      ),
    },
    {
      key: "quantity",
      label: "Available quantity",
      filter: (
        <MoreLessFilter
          less_than={quantity.less_than}
          more_than={quantity.more_than}
          setStateCallback={setQuantity}
          targetState={quantity}
        />
      ),
    },
    {
      key: "productAvailability",
      label: "Publishing",
      filter: (
        <ChoiceList
          choices={shopifyChannels || []} // Fallback to empty array if shopifyChannels is undefined
          onChange={useCallback(([value]) => {
            setProductAvailability(value);
          }, [])}
          selected={[productAvailability]}
          title=""
          titleHidden
        />
      ),
    },
    {
      key: "productTags",
      label: "Tags",
      filter: (
        <TagCheckList
          createNew={false}
          onAdd={handleNewTagAddition}
          onChange={setProductTags}
          selectedOptions={productTags}
        />
      ),
    },
    // {
    //   key: "horseLocations",
    //   label: "Location",
    //   filter: (
    //     <Select
    //       label=""
    //       labelHidden
    //       onChange={handleHorseLocationIdChange}
    //       options={horseLocations satisfies Option[]}
    //       value={horseLocationId?.toString() || allLocations}
    //     />
    //   ),
    // },
  ];

  const hvFilters = useMemo(() => {
    const appliedFilters: AppliedFilterInterface[] = [];

    if (!isEmpty(collectionId)) {
      const collection = collections.find((c) => c.id.toString() === collectionId);
      appliedFilters.push({
        key: "collection",
        label: collection ? `Collection: ${collection.title}` : `Collection: ${collectionId}`,
        onRemove: handleCollectionRemove,
      });
    }

    if (!isEmpty(vendor)) {
      appliedFilters.push({
        key: "vendor",
        label: disambiguateLabel({ key: "vendor", value: vendor }),
        onRemove: handleVendorRemove,
      });
    }

    if (!isEmpty(supplierId)) {
      appliedFilters.push({
        key: "supplierId",
        label: disambiguateLabel({ key: "supplierId", value: supplierId }),
        onRemove: handleSupplierIdRemove,
      });
    }
    if (!isEmpty(productType)) {
      appliedFilters.push({
        key: "productType",
        label: disambiguateLabel({ key: "productType", value: productType }),
        onRemove: handleProductTypeRemove,
      });
    }
    if (!isEmpty(price.more_than) || !isEmpty(price.less_than)) {
      appliedFilters.push({
        key: "price",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: price,
          label: "Price",
          prefix: getCurrencySymbol(userCurrency),
        }),
        onRemove: handlePriceRemove,
      });
    }
    if (!isEmpty(cost.more_than) || !isEmpty(cost.less_than)) {
      appliedFilters.push({
        key: "cost",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: cost,
          label: "Cost",
          prefix: getCurrencySymbol(userCurrency),
        }),
        onRemove: handleCostRemove,
      });
    }
    if (!isEmpty(productValue.more_than) || !isEmpty(productValue.less_than)) {
      appliedFilters.push({
        key: "productValue",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: productValue,
          label: "Value",
          prefix: getCurrencySymbol(userCurrency),
        }),
        onRemove: handleProductValueRemove,
      });
    }
    if (!isEmpty(weight.more_than) || !isEmpty(weight.less_than)) {
      appliedFilters.push({
        key: "weight",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: weight,
          label: "Weight",
          suffix: "g",
        }),
        onRemove: handleWeightRemove,
      });
    }
    if (!isEmpty(totalVolume.more_than) || !isEmpty(totalVolume.less_than)) {
      appliedFilters.push({
        key: "totalVolume",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: totalVolume,
          label: "Total volume",
          suffix: "m³",
        }),
        onRemove: handleTotalVolumeRemove,
      });
    }
    if (!isEmpty(quantity.more_than) || !isEmpty(quantity.less_than)) {
      appliedFilters.push({
        key: "quantity",
        label: disambiguateLabel({
          key: "moreThanLessThan",
          value: quantity,
          label: "Quantity",
        }),
        onRemove: handleQuantityRemove,
      });
    }
    if (!isEmpty(productAvailability) && !shopifyChannelsLoading && shopifyChannels) {
      const channelOption = shopifyChannels.find((channel) => channel.value === productAvailability);
      const badgeLabel = channelOption ? channelOption.label : `Unknown (${productAvailability})`;
      appliedFilters.push({
        key: "productAvailability",
        label: disambiguateLabel({ key: "productAvailability", value: badgeLabel }),
        onRemove: handleProductAvailabilityRemove,
      });
    }
    if (!isEmpty(productTags)) {
      appliedFilters.push({
        key: "productTags",
        label: disambiguateLabel({ key: "productTags", value: productTags }),
        onRemove: handleProductTagsRemove,
      });
    }
    if (!isEmpty(horseLocationId)) {
      appliedFilters.push({
        key: "horseLocations",
        label: disambiguateLabel({ key: "horseLocations", value: horseLocationId }),
        onRemove: handleHorseLocationRemove,
      });
    }

    return appliedFilters;
  }, [
    vendor,
    supplierId,
    productType,
    price,
    cost,
    productValue,
    weight,
    totalVolume,
    quantity,
    productAvailability,
    productTags,
    horseLocationId,
    userCurrency,
    disambiguateLabel,
    shopifyChannels,
    shopifyChannelsLoading,
    handleVendorRemove,
    handleSupplierIdRemove,
    handleProductTypeRemove,
    handlePriceRemove,
    handleCostRemove,
    handleProductValueRemove,
    handleWeightRemove,
    handleTotalVolumeRemove,
    handleQuantityRemove,
    handleProductAvailabilityRemove,
    handleProductTagsRemove,
    handleHorseLocationRemove,
    collectionId,
    handleCollectionRemove,
    collections,
  ]);

  useEffect(() => {
    if (hvFilters.length > 0) {
      setMode(IndexFiltersMode.Filtering); // Ensure filters appear expanded when applied
    }
  }, [hvFilters]);

  return (
    <IndexFilters
      appliedFilters={hvFilters}
      canCreateNewView={false}
      cancelAction={{
        onAction: () => {},
        disabled: false,
        loading: false,
      }}
      closeOnChildOverlayClick
      filters={filters}
      mode={mode}
      onClearAll={handleFiltersClearAll}
      onQueryChange={setSearch}
      onQueryClear={handleSearchRemove}
      onSelect={disableTabs ? undefined : onSelect}
      onSort={setSort}
      queryPlaceholder="Search variants"
      queryValue={search}
      selected={disableTabs ? undefined : selected}
      setMode={setMode}
      sortOptions={sortOptions}
      sortSelected={sort}
      tabs={disableTabs ? [] : tabs}
    />
  );
}

export const HorseVariantSearchFieldAndMoreFilters = React.memo(HorseVariantSearchFieldAndMoreFiltersComp);
